
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { debounce } from 'vue-debounce';
import AddressSuggestionsModal from '@/components/views/ClientView/AddressSuggestionsModal.vue';
import B2BDeliverySection from '@/components/views/ClientView/B2BDeliverySection.vue';
import AddressLocationFields from '@/components/views/ClientView/AddressLocationFields.vue';
import CountryRepository from '@/repositories/CountryRepository';
import { AddressSuggestion } from '@/modules/Client/Interfaces/AddressSuggestion';
import { parseAddressSuggestion } from '@/modules/Client/helpers/AddressSuggestionHelper';

interface B2BDeliveryData {
    phoneNumber: string;
    email: string;
    notes: string;
    deliveryDays: string[];
    languageCode: string;
    printRegion: string;
    deliverySchedule: string[];
    deliveryRegion: string;
}

@Component({
    name: 'ClientAddressForm',
    components: {
        AddressSuggestionsModal,
        B2BDeliverySection,
        AddressLocationFields,
    },
})
export default class ClientAddressForm extends Vue {
    @Prop({ default: null }) private value!: any;
    @Prop({ default: null }) private filteredAddressTypes!: any[];
    @Prop({ default: false }) private isContactAddress!: boolean;
    @Prop({ required: true }) private canAccessLogistics!: boolean;
    @Prop({ default: false }) private isAddressValidationUnavailable!: boolean;

    private isSuggestionsBoxVisible = false;
    private focusedInputDataTest: string | null = null;

    // Validation itself happens in NewEditClientView (validateAddressAndUpdateFormData)
    private debouncedValidation = debounce(() => {
        if (this.isContactAddress || this.isAddressValidationUnavailable) {
            return;
        }

        this.$emit('validate-address', this.value, () => this.restoreFocusedInput());
    }, 200);

    private onStreetAddressInput() {
        this.debouncedValidation();
        this.$emit('address-input', this.value);
    }

    private rememberFocusedInput(dataTest: string) {
        this.focusedInputDataTest = dataTest;
    }

    private restoreFocusedInput() {
        if (!this.focusedInputDataTest) {
            return;
        }

        this.$nextTick(() => {
            const input = this.$el.querySelector(
                `input[data-test="${this.focusedInputDataTest}"], [data-test="${this.focusedInputDataTest}"] input`,
            ) as HTMLInputElement | null;

            if (input && document.activeElement === document.body) {
                input.focus();
            }
        });
    }

    private onPoNumberChange(value: string) {
        this.value.poNumber = value;
    }

    private onCityChange(value: string) {
        this.value.city = value;
    }

    @Watch('value.validationSuggestions')
    private onValidationSuggestionsChange(suggestions: any[]) {
        this.isSuggestionsBoxVisible = !this.isAddressValidationUnavailable && !!(suggestions && suggestions.length);
    }

    @Watch('isAddressValidationUnavailable')
    private onAddressValidationAvailabilityChange(isUnavailable: boolean) {
        if (isUnavailable) {
            this.isSuggestionsBoxVisible = false;
        }
    }

    private selectSuggestion(suggestion: AddressSuggestion) {
        const parsedAddress = parseAddressSuggestion(suggestion);
        this.value.streetNameAndNumber = parsedAddress.streetNameAndNumber;
        this.value.poNumber = parsedAddress.poNumber;
        this.value.city = parsedAddress.city;

        this.$set(this.value, 'validated', true);
        this.value.validationSuggestions = [];
        this.isSuggestionsBoxVisible = false;
    }

    private closeSuggestionsPopup() {
        this.value.validationSuggestions = [];
        this.isSuggestionsBoxVisible = false;
    }

    private onB2BDeliveryChange(payload: { isB2BDelivery: boolean; data: B2BDeliveryData }) {
        const { isB2BDelivery, data } = payload;
        this.value.b2bDeliveryPhone = isB2BDelivery ? data.phoneNumber : '';
        this.value.b2bDeliveryEmail = isB2BDelivery ? data.email : '';
        this.value.b2bDeliveryNotes = isB2BDelivery ? data.notes : '';
        this.value.languageCode = isB2BDelivery ? data.languageCode : '';
        this.value.printRegion = isB2BDelivery ? data.printRegion : '';
        this.value.b2bDeliveryDays = isB2BDelivery ? (data.deliveryDays?.length ? data.deliveryDays : []) : [];
        this.value.b2bDeliverySchedule = isB2BDelivery ? data.deliverySchedule : '';
        this.value.b2bDeliveryRegion = isB2BDelivery ? data.deliveryRegion : '';
        this.$emit('input', this.value);
    }

    get countries() {
        return CountryRepository.getAll();
    }

    get internalCodeOptions() {
        const seen = new Set();
        const options: { code: string; label: string }[] = [];
        this.countries
            .filter((c) => c.internalCode && c.internalCode !== 'null' && c.internalCode !== '')
            .forEach((c) => {
            if (!seen.has(c.internalCode)) {
                seen.add(c.internalCode);
                options.push({ code: c.internalCode, label: `${c.internalCode}` });
            }
            });
        return options.sort((a, b) => a.code.localeCompare(b.code));
    }

    get languageOptions() {
        const seen = new Set();
        const options: { code: string; label: string }[] = [];
        this.countries
            .filter(
            (c) =>
                c.languageCode &&
                c.languageCode !== 'null' &&
                c.languageCode !== '' &&
                c.internalCode &&
                c.internalCode !== 'null' &&
                c.internalCode !== ''
            )
            .forEach((c) => {
            if (!seen.has(c.code)) {
                seen.add(c.code);
                options.push({ code: c.languageCode, label: `${c.languageCode} - ${c.code.toUpperCase()}` });
            }
            });
        return options.sort((a, b) => a.label.localeCompare(b.label));
    }

    get regionOptions() {
        const regions = this.countries.map((c) => c.printRegion).filter((val) => val && val !== 'null' && val !== '');
        return [...new Set(regions)].sort();
    }
}
